import { DesktopModalHeader } from '@molecules/DesktopModalHeader';
import { TagCategorySection } from '@molecules/TagCategorySection';
import {
  useGetCanonicalCategoriesByEvent,
  useGetCanonicalTagsByEvent,
} from '@sunrise-ui/api-client';
import { SpeakerFilter } from '@sunrise-ui/composites';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  Button,
  ButtonVariants,
  Loader,
  Modal,
  TextBold,
  TextReg,
  Toggle,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
  useSafeLocalSearchParams,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

type Props = {
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  filterCategoryIds: Set<string>;
  setFilterCategoryIds: (categoryIds: Set<string>) => void;
  filterTagIds: Set<string>;
  setFilterTagIds: (tagIds: Set<string>) => void;
  selectedSpeakerId?: string | undefined;
  setSelectedSpeakerId?: (speakerId: string | undefined) => void;
  showViewedOnly?: boolean;
  setShowViewedOnly?: (show: boolean) => void;
};

export function SessionFilter({
  filterCategoryIds,
  setFilterCategoryIds,
  filterTagIds,
  setFilterTagIds,
  isFilterOpen,
  setIsFilterOpen,
  selectedSpeakerId,
  setSelectedSpeakerId,
  showViewedOnly,
  setShowViewedOnly,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const { isMobile } = useMediaQueries();
  const { eventId } = useSafeLocalSearchParams<{
    eventId: string;
  }>();
  const { isAuthenticated } = useAuthStore();

  // Combine both category and tag IDs for the selected state
  const allSelectedIds = new Set([...filterCategoryIds, ...filterTagIds]);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(allSelectedIds);
  const { data: tags, isLoading: isLoadingTags } =
    useGetCanonicalTagsByEvent(eventId);
  const { data: categories, isLoading: isLoadingCategories } =
    useGetCanonicalCategoriesByEvent(eventId);

  // Get all available filter options (both categories and tags)
  const allFilterOptions = [
    ...(categories
      ?.map((cat) => cat.id)
      .filter((id): id is string => Boolean(id)) ?? []),
    ...(tags?.map((tag) => tag.id).filter((id): id is string => Boolean(id)) ??
      []),
  ];

  const isLoadingData = isLoadingTags || isLoadingCategories;

  const handleUpdateFilterTags =
    (newSelectedTags: Set<string> = allSelectedIds) =>
    () => {
      // Separate the selected IDs into categories and tags
      const newCategoryIds = new Set<string>();
      const newTagIds = new Set<string>();

      newSelectedTags.forEach((id) => {
        // Check if this ID belongs to a category
        const isCategory = categories?.some((cat) => cat.id === id);
        if (isCategory) {
          newCategoryIds.add(id);
        } else {
          // Otherwise it's a tag
          newTagIds.add(id);
        }
      });

      setIsFilterOpen(false);
      setFilterCategoryIds(newCategoryIds);
      setFilterTagIds(newTagIds);
      setSelectedTags(newSelectedTags);
    };

  const handleTagPress = (id?: string) => {
    if (!id) return;
    const newSelectedTags = new Set(selectedTags);
    if (newSelectedTags.has(id)) newSelectedTags.delete(id);
    else newSelectedTags.add(id);

    setSelectedTags(newSelectedTags);
  };

  const showTagBackground = (id?: string) => {
    return Boolean(id && selectedTags.has(id));
  };

  return (
    <Modal
      visible={isFilterOpen}
      hide={handleUpdateFilterTags()}
      header={
        isMobile ? (
          <TextReg style={styles.mobileHeader}>Filter Sessions</TextReg>
        ) : (
          <DesktopModalHeader
            title="Filter Sessions"
            onClose={handleUpdateFilterTags()}
          />
        )
      }
      fullscreenOnBreakpoint={false}
      contentStyle={styles.modalContent}
    >
      <View style={styles.container}>
        {isLoadingData && <Loader />}
        {!isLoadingData && (
          <ScrollView contentContainerStyle={styles.scrollContent}>
            {/* All/Viewed Toggle for mobile authenticated users */}
            {isMobile && isAuthenticated && setShowViewedOnly && (
              <View>
                <TextBold style={styles.sectionTitle}>Sessions</TextBold>
                <Toggle
                  value={showViewedOnly || false}
                  onValueChange={setShowViewedOnly}
                  leftLabel="All"
                  rightLabel="Viewed"
                />
              </View>
            )}

            {/* Speaker Search for Mobile */}
            {isMobile && setSelectedSpeakerId && (
              <View>
                <TextBold style={styles.sectionTitle}>Speakers</TextBold>
                <SpeakerFilter
                  eventId={eventId}
                  selectedSpeakerId={selectedSpeakerId}
                  onSpeakerSelect={setSelectedSpeakerId}
                />
              </View>
            )}

            {/* Categories as "Tracks" */}
            {categories && categories.length > 0 && (
              <TagCategorySection
                categoryName="Tracks"
                onTagPress={handleTagPress}
                showTagBackground={showTagBackground}
                tags={categories
                  .map((category) => ({
                    id: category.id,
                    name: category.name,
                    session_id: '', // Not used by TagCategorySection, but required by interface
                    tag_id: category.id || '', // Not used by TagCategorySection, but required by interface
                  }))
                  .filter((cat) => cat.id)}
              />
            )}

            {/* Tags as "Industries" */}
            {tags && tags.length > 0 && (
              <TagCategorySection
                categoryName="Industries"
                onTagPress={handleTagPress}
                showTagBackground={showTagBackground}
                tags={tags
                  .map((tag) => ({
                    id: tag.id,
                    name: tag.name,
                    session_id: '', // Not used by TagCategorySection, but required by interface
                    tag_id: tag.id || '', // Not used by TagCategorySection, but required by interface
                  }))
                  .filter((tag) => tag.id)}
              />
            )}
          </ScrollView>
        )}
        <View style={styles.buttonContainer}>
          <Button
            disabled={isLoadingData}
            onPress={() => {
              setSelectedTags(new Set(allFilterOptions));
            }}
            size={isMobile ? 'small' : 'medium'}
            labelStyle={styles.buttonLabel}
          >
            Select All
          </Button>
          <Button
            disabled={isLoadingData}
            onPress={() => {
              setSelectedTags(new Set());
            }}
            size={isMobile ? 'small' : 'medium'}
            labelStyle={styles.buttonLabel}
          >
            Clear
          </Button>
          <Button
            onPress={handleUpdateFilterTags(selectedTags)}
            variant={ButtonVariants.PURPLE}
            size={isMobile ? 'small' : 'medium'}
            labelStyle={styles.buttonLabel}
          >
            <TextReg>Apply</TextReg>
          </Button>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    scrollContent: {
      columnGap: isMobile ? 16 : 48, // Increased column gap to add more space between the lists
      rowGap: isMobile ? 16 : 32,
      marginHorizontal: isMobile ? 12 : 24,
      alignItems: 'flex-start', // Align items at the top
      justifyContent: 'center', // Center items horizontally
      flexDirection: isMobile ? 'column' : 'row',
      flexWrap: 'wrap',
    },
    modalContent: {
      padding: 0,
      paddingTop: isMobile ? 12 : 24,
      paddingBottom: isMobile ? 12 : 24,
    },
    mobileHeader: {
      fontSize: 16,
      textAlign: 'center',
      paddingVertical: 12,
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      paddingRight: isMobile ? 12 : 24,
      paddingTop: isMobile ? 12 : 24,
      gap: isMobile ? 8 : 12,
    },
    buttonLabel: {
      marginLeft: isMobile ? 12 : undefined,
      marginRight: isMobile ? 12 : undefined,
    },
    sectionContainer: {
      marginBottom: 16,
      paddingHorizontal: isMobile ? 12 : 24,
    },
    sectionTitle: {
      fontSize: 16,
    },
    placeholder: {
      fontSize: 14,
      opacity: 0.7,
      fontStyle: 'italic',
    },
  });
